import { useState, type FormEvent, type JSX } from 'react';
import type { Monitor, MonitorKind } from '@netdeck/contracts';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { Field } from '../../shared/ui/Field';
import { HelpNote } from '../../shared/ui/HelpNote';

/** Порты по умолчанию — чтобы не заставлять вспоминать очевидное. */
const DEFAULT_PORTS: Partial<Record<MonitorKind, number>> = {
  tcp: 22,
  http: 80,
  snmp: 161,
};

const KIND_LABELS: Record<MonitorKind, string> = {
  icmp: 'ICMP',
  tcp: 'TCP',
  http: 'HTTP',
  snmp: 'SNMP',
};

export interface MonitorFormValue {
  kind: MonitorKind;
  /** Пусто — брать адрес узла. */
  target: string;
  port: number | null;
  intervalSec: number;
  timeoutMs: number;
  failThreshold: number;
  okThreshold: number;
  config?: Record<string, unknown>;
}

export interface MonitorFormProps {
  mode: 'create' | 'edit';
  /** Заполняется при правке. */
  monitor?: Monitor;
  nodeHasAddress: boolean;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (value: MonitorFormValue) => void;
}

/**
 * Форма монитора — одна на создание и правку.
 *
 * Тип проверки при правке не меняется: у каждого типа своя конфигурация,
 * и смена типа на месте оставила бы монитор с чужими параметрами. Проще
 * удалить и создать заново — так же устроен и контракт (§7.5).
 */
export function MonitorForm({
  mode,
  monitor,
  nodeHasAddress,
  pending,
  error,
  onCancel,
  onSubmit,
}: MonitorFormProps): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [kind, setKind] = useState<MonitorKind>(monitor?.kind ?? 'icmp');
  const [target, setTarget] = useState(monitor?.target ?? '');
  const [port, setPort] = useState(String(monitor?.port ?? DEFAULT_PORTS[kind] ?? ''));
  const [intervalSec, setIntervalSec] = useState(String(monitor?.intervalSec ?? 30));
  const [timeoutMs, setTimeoutMs] = useState(String(monitor?.timeoutMs ?? 2000));
  const [failThreshold, setFailThreshold] = useState(String(monitor?.failThreshold ?? 3));
  const [okThreshold, setOkThreshold] = useState(String(monitor?.okThreshold ?? 2));
  const [config, setConfig] = useState<Record<string, string>>(() => configToStrings(monitor));

  const portNeeded = kind !== 'icmp';
  const interval = Number(intervalSec);
  const timeout = Number(timeoutMs);

  // Ровно то же правило, что и на сервере: висящая проверка не должна
  // перекрывать следующую. Показать это до отправки честнее, чем ответить 400.
  const timeoutTooBig = timeout > interval * 1000;
  const intervalTooSmall = interval < 5;

  function submit(event: FormEvent): void {
    event.preventDefault();

    onSubmit({
      kind,
      target: target.trim(),
      port: portNeeded ? Number(port || DEFAULT_PORTS[kind] || 0) : null,
      intervalSec: interval,
      timeoutMs: timeout,
      failThreshold: Number(failThreshold),
      okThreshold: Number(okThreshold),
      ...(kind === 'http' || kind === 'snmp' ? { config: configFromStrings(kind, config) } : {}),
    });
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 border-b border-line bg-row-alt px-3.5 py-3"
    >
      <div className="flex flex-wrap items-start gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm text-muted">{t('monitors.kindLabel')}</span>
          <select
            value={kind}
            disabled={mode === 'edit'}
            title={mode === 'edit' ? t('monitors.kindFixed') : undefined}
            onChange={(event) => {
              const next = event.target.value as MonitorKind;
              setKind(next);
              setPort(String(DEFAULT_PORTS[next] ?? ''));
              setConfig({});
            }}
            className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg disabled:text-muted"
          >
            {Object.entries(KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {portNeeded ? (
          <div className="w-full sm:w-[90px]">
            <Field
              label={t('agents.probe.port')}
              required
              mono
              value={port}
              onChange={(event) => setPort(event.target.value)}
              placeholder="22"
            />
          </div>
        ) : null}
      </div>

      <Field
        label={t('monitors.target')}
        mono
        value={target}
        onChange={(event) => setTarget(event.target.value)}
        placeholder={nodeHasAddress ? t('monitors.targetPlaceholder') : '10.20.0.11'}
        hint={nodeHasAddress ? t('monitors.targetHint') : t('monitors.targetRequired')}
      />

      <div className="flex flex-wrap items-start gap-3">
        <div className="w-full sm:w-[100px]">
          <Field
            label={t('monitors.interval')}
            required
            mono
            value={intervalSec}
            onChange={(event) => setIntervalSec(event.target.value)}
            hint={t('monitors.intervalHint', { min: 5 })}
          />
        </div>

        <div className="w-full sm:w-[110px]">
          <Field
            label={t('monitors.timeout')}
            required
            mono
            value={timeoutMs}
            onChange={(event) => setTimeoutMs(event.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center pt-5">
          <HelpNote topic="monitorThresholds" anchor="monitorThresholds" />
        </div>

        <div className="w-full sm:w-[110px]">
          <Field
            label={t('monitors.failThreshold')}
            required
            mono
            value={failThreshold}
            onChange={(event) => setFailThreshold(event.target.value)}
            hint={t('monitors.failThresholdHint')}
          />
        </div>

        <div className="w-full sm:w-[120px]">
          <Field
            label={t('monitors.okThreshold')}
            required
            mono
            value={okThreshold}
            onChange={(event) => setOkThreshold(event.target.value)}
            hint={t('monitors.okThresholdHint')}
          />
        </div>
      </div>

      {kind === 'http' ? (
        <HttpConfigFields config={config} onChange={setConfig} />
      ) : kind === 'snmp' ? (
        <SnmpConfigFields config={config} onChange={setConfig} />
      ) : null}

      {timeoutTooBig ? (
        <div className="text-sm text-down-fg">{t('monitors.timeoutTooBig')}</div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          icon="check"
          type="submit"
          variant="primary"
          size="sm"
          disabled={
            pending || intervalTooSmall || timeoutTooBig || (portNeeded && !port) || !interval
          }
        >
          {pending
            ? t('sites.form.saving')
            : mode === 'create'
              ? t('monitors.create')
              : t('common.action.save')}
        </Button>
        <Button icon="close" size="sm" onClick={onCancel} disabled={pending}>
          {t('common.action.cancel')}
        </Button>
        {error ? <span className="text-sm text-down-fg">{errorText(error)}</span> : null}
      </div>
    </form>
  );
}

function HttpConfigFields({
  config,
  onChange,
}: {
  config: Record<string, string>;
  onChange: (config: Record<string, string>) => void;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 border-t border-line-subtle pt-3">
      <Field
        label={t('monitors.http.path')}
        mono
        value={config['path'] ?? '/'}
        onChange={(event) => onChange({ ...config, path: event.target.value })}
        placeholder="/health"
      />

      <div className="flex flex-wrap items-start gap-3">
        <div className="w-full sm:w-[140px]">
          <Field
            label={t('monitors.http.expectStatus')}
            mono
            value={config['expectStatus'] ?? '200'}
            onChange={(event) => onChange({ ...config, expectStatus: event.target.value })}
            hint={t('monitors.http.commaSeparated')}
          />
        </div>

        <label className="flex items-center gap-2 pt-6 text-sm text-muted">
          <input
            type="checkbox"
            checked={config['tls'] === 'true'}
            onChange={(event) => onChange({ ...config, tls: String(event.target.checked) })}
          />
          HTTPS
        </label>

        <label className="flex items-center gap-2 pt-6 text-sm text-muted">
          <input
            type="checkbox"
            checked={config['followRedirects'] === 'true'}
            onChange={(event) =>
              onChange({ ...config, followRedirects: String(event.target.checked) })
            }
          />
          {t('monitors.http.followRedirects')}
        </label>
      </div>

      <Field
        label={t('monitors.http.expectBody')}
        value={config['expectBody'] ?? ''}
        onChange={(event) => onChange({ ...config, expectBody: event.target.value })}
        hint={t('monitors.http.expectBodyHint')}
      />
    </div>
  );
}

function SnmpConfigFields({
  config,
  onChange,
}: {
  config: Record<string, string>;
  onChange: (config: Record<string, string>) => void;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 border-t border-line-subtle pt-3">
      <Field
        label="OID"
        required
        mono
        value={config['oid'] ?? ''}
        onChange={(event) => onChange({ ...config, oid: event.target.value })}
        placeholder="1.3.6.1.2.1.1.3.0"
        hint={t('monitors.snmp.oidHint')}
      />

      <div className="flex flex-wrap items-start gap-3">
        <div className="w-full sm:w-[150px]">
          <Field
            label="Community"
            mono
            value={config['community'] ?? 'public'}
            onChange={(event) => onChange({ ...config, community: event.target.value })}
          />
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm text-muted">{t('monitors.snmp.version')}</span>
          <select
            value={config['version'] ?? '2c'}
            onChange={(event) => onChange({ ...config, version: event.target.value })}
            className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg"
          >
            <option value="2c">2c</option>
            <option value="1">1</option>
          </select>
        </label>
      </div>

      <Field
        label={t('monitors.snmp.expectValue')}
        mono
        value={config['expectValue'] ?? ''}
        onChange={(event) => onChange({ ...config, expectValue: event.target.value })}
        hint={t('monitors.snmp.expectValueHint')}
      />
    </div>
  );
}

/** Конфигурация монитора приходит с сервера как JSON; в форме она строковая. */
function configToStrings(monitor: Monitor | undefined): Record<string, string> {
  if (!monitor) {
    return {};
  }

  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(monitor.config)) {
    // В форму попадают только скалярные значения и списки кодов ответа:
    // вложенных объектов в конфигурации проверок нет, а если появятся —
    // им понадобится своё поле, а не подпись «[object Object]».
    if (Array.isArray(value)) {
      result[key] = value.join(',');
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = String(value);
    }
  }

  return result;
}

/**
 * Обратное преобразование. Пустые значения не отправляются: для сервера
 * пустая строка и «не задано» — разные вещи, а пользователь, очистив поле,
 * имеет в виду именно второе.
 */
function configFromStrings(
  kind: MonitorKind,
  config: Record<string, string>,
): Record<string, unknown> {
  if (kind === 'snmp') {
    return {
      oid: (config['oid'] ?? '').trim(),
      community: (config['community'] ?? 'public').trim() || 'public',
      version: config['version'] ?? '2c',
      expectValue: config['expectValue']?.trim() ? config['expectValue'].trim() : null,
    };
  }

  const statuses = (config['expectStatus'] ?? '200')
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isInteger(value) && value >= 100 && value <= 599);

  return {
    path: (config['path'] ?? '/').trim() || '/',
    method: 'GET',
    expectStatus: statuses.length > 0 ? statuses : [200],
    expectBody: config['expectBody']?.trim() ? config['expectBody'].trim() : null,
    followRedirects: config['followRedirects'] === 'true',
    tls: config['tls'] === 'true',
  };
}
